<template>
  <div :class="noticeClasses" :role="noticeRole">
    <div v-if="title" class="inline-notice__title">
      <i v-if="showIcon" :class="[resolvedIcon, 'inline-notice__icon']" aria-hidden="true"/>
      <span>{{ title }}</span>
    </div>

    <i v-else-if="showIcon"
       :class="[resolvedIcon, 'inline-notice__icon']"
       aria-hidden="true"/>

    <div class="inline-notice__content">
      <div class="inline-notice__body">
        <slot/>
      </div>

      <div v-if="$slots.actions" class="inline-notice__actions">
        <slot name="actions"/>
      </div>
    </div>
  </div>
</template>

<script>
const severityIcons = {
  info: 'pi pi-info-circle',
  warning: 'pi pi-exclamation-triangle',
  error: 'pi pi-exclamation-triangle',
  success: 'pi pi-check-circle',
}

export default {
  name: 'InlineNotice',
  props: {
    severity: {
      type: String,
      default: 'info',
      validator: value => ['info', 'warning', 'error', 'success'].includes(value),
    },
    title: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: null,
    },
    showIcon: {
      type: Boolean,
      default: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'accent-only'].includes(value),
    },
  },
  computed: {
    resolvedIcon() {
      return this.icon || severityIcons[this.severity]
    },
    noticeClasses() {
      return [
        'inline-notice',
        `inline-notice--${this.severity}`,
        {
          'inline-notice--compact': this.compact,
          'inline-notice--has-title': !!this.title,
          'inline-notice--accent-only': this.variant === 'accent-only',
        },
      ]
    },
    noticeRole() {
      return this.severity === 'error' ? 'alert' : 'status'
    },
  },
}
</script>

<style scoped lang="scss">
.inline-notice {
  display: flex;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
  gap: 6px;
  padding: 1rem;
  border: 1px solid;
  border-left-width: 4px;
  border-radius: 3px;
  color: var(--text-color);

  &--compact {
    padding: 0.75rem;
  }

  &--has-title {
    display: block;
  }

  &--accent-only {
    border-width: 0 0 0 4px;
    border-radius: 0 3px 3px 0;
  }

  &--info {
    border-color: var(--blue-500, #2196f3);
    background: var(--blue-50, #e3f2fd);
    color: var(--blue-900, #0d47a1);

    .inline-notice__icon {
      color: var(--blue-600, #1e88e5);
    }
  }

  &--warning {
    border-color: var(--yellow-500, #fbc02d);
    background: var(--yellow-50, #fffde7);
    color: var(--yellow-900, #5f4b00);

    .inline-notice__icon {
      color: var(--yellow-700, #f9a825);
    }
  }

  &--error {
    border-color: var(--red-500, #f44336);
    background: var(--red-50, #ffebee);
    color: var(--red-600, #e53935);

    .inline-notice__icon {
      color: var(--red-600, #e53935);
    }
  }

  &--success {
    border-color: var(--green-500, #4caf50);
    background: var(--green-50, #e8f5e9);
    color: var(--green-900, #1b5e20);

    .inline-notice__icon {
      color: var(--green-600, #43a047);
    }
  }
}

.inline-notice__icon {
  flex: 0 0 auto;
  font-size: 1.2rem;
}

.inline-notice__content {
  min-width: 0;
  flex: 1 1 auto;
}

.inline-notice__title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.inline-notice__body {
  line-height: 1.45;
}

.inline-notice__actions {
  margin-top: 1.2rem;
}

.inline-notice--warning {
  :deep(.p-checkbox .p-checkbox-box:not(.p-highlight)) {
    background: #fff;
    border-color: var(--yellow-700, #f9a825);
  }
}
</style>
